import api from '../../../lib/api';
import type {
  CreateTelegramChannelRequest,
  TelegramChannel,
  TelegramHealthResult,
  TelegramUserWhitelistEntry,
  UpdateTelegramChannelRequest,
} from '@workspace/types';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export const telegramAdminApi = {
  async getChannelByInstanceBot(instanceBotId: string): Promise<TelegramChannel | null> {
    const channels = await this.listChannels();
    return channels.find(channel => channel.instanceBotId === instanceBotId) ?? null;
  },

  async listChannels(): Promise<TelegramChannel[]> {
    const response = await api.get<ApiResponse<TelegramChannel[]>>('/admin/telegram/channels');
    return requireData(response, 'No se pudieron cargar los canales Telegram');
  },

  async createChannel(payload: CreateTelegramChannelRequest): Promise<TelegramChannel> {
    const response = await api.post<ApiResponse<TelegramChannel>>('/admin/telegram/channels', payload);
    return requireData(response, 'No se pudo crear el canal Telegram');
  },

  async updateChannel(id: string, payload: UpdateTelegramChannelRequest): Promise<TelegramChannel> {
    const response = await api.patch<ApiResponse<TelegramChannel>>(
      `/admin/telegram/channels/${id}`,
      payload
    );
    return requireData(response, 'No se pudo actualizar el canal Telegram');
  },

  async deleteChannel(id: string): Promise<void> {
    await api.delete(`/admin/telegram/channels/${id}`);
  },

  async healthCheck(id: string): Promise<TelegramHealthResult> {
    const response = await api.post<ApiResponse<TelegramHealthResult>>(
      `/admin/telegram/channels/${id}/health-check`
    );
    return requireData(response, 'No se pudo verificar el canal');
  },

  async rotateSecret(id: string): Promise<{ secret: string }> {
    const response = await api.post<ApiResponse<{ secret: string }>>(
      `/admin/telegram/channels/${id}/rotate-secret`
    );
    return requireData(response, 'No se pudo rotar el secreto');
  },

  async listWhitelist(channelId: string): Promise<TelegramUserWhitelistEntry[]> {
    const response = await api.get<ApiResponse<TelegramUserWhitelistEntry[]>>(
      `/admin/telegram/channels/${channelId}/whitelist`
    );
    return requireData(response, 'No se pudo cargar la whitelist');
  },

  async addUser(
    channelId: string,
    telegramUserId: string,
    label?: string
  ): Promise<TelegramUserWhitelistEntry> {
    const response = await api.post<ApiResponse<TelegramUserWhitelistEntry>>(
      `/admin/telegram/channels/${channelId}/whitelist`,
      { telegramUserId, label }
    );
    return requireData(response, 'No se pudo agregar el usuario');
  },

  async removeUser(channelId: string, userId: string): Promise<void> {
    await api.delete(
      `/admin/telegram/channels/${channelId}/whitelist/${encodeURIComponent(userId)}`
    );
  },
};
